import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  MapPin,
  Plus,
  Edit,
  Trash2,
  Home,
  Building2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import AddressForm from "./address-form";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  getAddresses,
  updateAddress,
  deleteAddress,
  selectAddresses,
  selectAddressesLoading,
} from "@/store/slices/authSlice";
import Loader from "@/components/loader/loader";

export default function Addresses() {
  const dispatch = useAppDispatch();
  const addresses = useAppSelector(selectAddresses);
  const isLoading = useAppSelector(selectAddressesLoading);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);

  // Load addresses on mount
  useEffect(() => {
    dispatch(getAddresses());
  }, [dispatch]);

  const handleAddAddress = async () => {
    try {
      // Refresh addresses after add
      await dispatch(getAddresses());
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Failed to refresh addresses after add:", error);
    }
  };

  const handleEditAddress = async (updatedAddress) => {
    try {
      await dispatch(updateAddress({ id: updatedAddress.id, data: updatedAddress })).unwrap();
      // Refresh addresses after update
      await dispatch(getAddresses());
      setIsDialogOpen(false);
      setEditingAddress(null);
    } catch {
      // Error handled in thunk
    }
  };

  const handleDeleteAddress = async (id) => {
    try {
      await dispatch(deleteAddress(id)).unwrap();
      // Refresh addresses after delete
      await dispatch(getAddresses());
    } catch {
      // Error handled in thunk
    }
  };

  const openEditDialog = (address) => {
    setEditingAddress(address);
    setIsDialogOpen(true);
  };

  const openAddDialog = () => {
    setEditingAddress(null);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingAddress(null);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex items-center gap-4">
        <MapPin className="w-8 h-8 text-primary" />
        <h1 className="text-3xl font-bold text-primary">My Addresses</h1>
      </div>
      <p className="text-gray-600">
        Manage your shipping and billing addresses.
      </p>

      {/* Add New Address Button */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogTrigger asChild>
          <Button
            className="w-full md:w-auto bg-primary hover:bg-primary/90 text-white"
            onClick={openAddDialog}
          >
            <Plus className="w-4 h-4 mr-2" /> Add New Address
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              {editingAddress ? "Edit Address" : "Add New Address"}
            </DialogTitle>
          </DialogHeader>
          <AddressForm
            address={editingAddress}
            onSubmit={editingAddress ? handleEditAddress : handleAddAddress}
            onCancel={handleDialogClose}
          />
        </DialogContent>
      </Dialog>

      {/* Loading State */}
      {isLoading ? (
        <div className="flex justify-center items-center h-40">
          <Loader className="animate-spin w-6 h-6 text-primary" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {addresses.length > 0 ? (
            addresses.map((address) => (
              <Card key={address.id}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-primary text-lg">
                    {address.name}
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(address)}
                    >
                      <Edit className="w-4 h-4 text-gray-500 hover:text-secondary" />
                      <span className="sr-only">Edit address</span>
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <Trash2 className="w-4 h-4 text-gray-500 hover:text-red-500" />
                          <span className="sr-only">Delete address</span>
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Address</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete this address? This
                            action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-red-600 hover:bg-red-700 text-white"
                            onClick={() => handleDeleteAddress(address.id)}
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardHeader>
                <CardContent className="space-y-1 text-gray-700">
                  <p>{address.address1}</p>
                  {address.address2 && <p>{address.address2}</p>}
                  <p>
                    {address.city}, {address.state} {address.zip}
                  </p>
                  <p>{address.country}</p>
                  <div className="flex flex-wrap gap-2 pt-2">
                    {address.isDefaultShipping && (
                      <Badge className="bg-secondary/10 text-secondary flex items-center gap-1">
                        <Home className="w-3 h-3" /> Default Shipping
                      </Badge>
                    )}
                    {address.isDefaultBilling && (
                      <Badge className="bg-secondary/10 text-secondary flex items-center gap-1">
                        <Building2 className="w-3 h-3" /> Default Billing
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="md:col-span-2 text-center py-8">
              <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg mb-4">
                No addresses found. Add your first address!
              </p>
              <Button
                className="bg-primary hover:bg-primary/90 text-white"
                onClick={openAddDialog}
              >
                <Plus className="w-4 h-4 mr-2" /> Add New Address
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
